import i18next from 'i18next';
import {
  DECLINE_REASON_NO_SHOW,
  DECLINE_REASON_BY_CLIENT,
} from 'utils/reservation';
import { convertSavedReservationToNormalized } from 'utils/reservation';
import type { ISavedReservation, ReservationConfig } from 'types';
import { TReservationCalendarView } from './ReservationsCalendar/ReservationsCalendar';

export const getMapLabelsToDeclineResons = (): Record<number, string> => ({
  [DECLINE_REASON_BY_CLIENT]: i18next.t('reservations-lang.cancelled-by-client'),
  [DECLINE_REASON_NO_SHOW]: i18next.t('reservations-lang.no-show'),
});

export const getResCancelled = () => i18next.t('reservations-lang.reservation-cancelled');
export const RES_CANCELLED_TEXT = (res: ISavedReservation) =>
  `${res.reserved_on} ${i18next.t('reservations-lang.guests-abbr', { count: res.guests_number })} ${res.customer_name}`;
export const getNewRes = () => i18next.t('reservations-lang.new-reservation');
export const NEW_RES_TEXT = (res: ISavedReservation) =>
  `${res.reserved_on} ${i18next.t('reservations-lang.guests-abbr', { count: res.guests_number })} ${res.customer_name}`;

const getTablePart = (item: ISavedReservation) => {
  if (!item.tables?.length) {
    return '';
  }
  const tableNames = item.tables.map((table) => table.name).join(', ');
  return tableNames;
};

const getFirstWord = (str: string) => {
  if (str && str.split) {
    const parts = str.split(' ');
    return parts[0];
  }
  return str;
};

const getNotes = (
  reservation: ISavedReservation,
  options: { showNotes?: boolean }
) => {
  if (!options?.showNotes) {
    return '';
  }

  if (reservation.notes_from_guest && reservation.notes_from_restaurant) {
    return ` | ${i18next.t('reservations-lang.notes-from-guest', { note: reservation.notes_from_guest })} | ${i18next.t('reservations-lang.notes-from-restaurant', { note: reservation.notes_from_restaurant })}`;
  }
  if (reservation.notes_from_restaurant) {
    return ` | ${i18next.t('reservations-lang.notes-label', { note: reservation.notes_from_restaurant })}`;
  }
  return reservation.notes_from_guest
    ? ` | ${i18next.t('reservations-lang.notes-label', { note: reservation.notes_from_guest })}`
    : '';
};

type TFormatReservationParams = {
  reservation: ISavedReservation;
  config: ReservationConfig;
  showNotes: boolean;
  view: TReservationCalendarView;
};

export const formatReservationTitle = (
  params: TFormatReservationParams
): string => {
  const { reservation, config, showNotes, view } = params;

  const formattedNotes = getNotes(reservation, { showNotes });
  const { visitTime: time } = convertSavedReservationToNormalized(
    reservation,
    config
  );

  const firstWordOfName = getFirstWord(reservation.customer_name);
  const fullName = reservation.customer_name;

  const guests = i18next.t('reservations-lang.guests-abbr', { count: reservation.guests_number }); // formatGuestsWithKids(reservation, config) if kids supported
  const tables = getTablePart(reservation);

  if (view === 'dayGridDay') {
    return `${time} | ${tables} | ${fullName} ${i18next.t('reservations-lang.for-guests', { count: reservation.guests_number })}${formattedNotes}`;
  }
  if (view === 'resourceTimelineDay' || view === 'resourceTimeGridDay') {
    return `${time} | ${tables} | ${firstWordOfName} - ${guests}${formattedNotes}`;
  }

  if (view === 'dayGridWeek') {
    return `${time} | ${tables} | ${fullName} ${i18next.t('reservations-lang.for-guests', { count: reservation.guests_number })}`;
  }
  if (view === 'dayGridMonth') {
    return `${time} | ${guests}`;
  }
  throw new Error('Unknown view');
};
